import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { blogApi, homeApi, pagesApi } from "../services/api";
import HeroSection from "../components/HeroSection";
import { mediaService } from "../services/mediaService";

function InsightsPage() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [pageData, setPageData] = useState({});
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    fetchBlogPosts();
    initializeCategories();
    fetchPageData();
  }, []);

  const fetchPageData = async () => {
    try {
      const response = await pagesApi.getByPageType("insights");
      setPageData(response.data || {});
    } catch (error) {
      console.error("Error loading page data:", error);
    }
  };

  useEffect(() => {
    if (pageData?.use_slider && pageData?.slider_images?.length > 0) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % pageData.slider_images.length);
      }, pageData.slider_interval || 5000);
      return () => clearInterval(interval);
    }
  }, [pageData]);

  const initializeCategories = async () => {
    try {
      // Fetch published blog posts to extract unique categories
      const response = await blogApi.getAll(true);
      const blogPosts = response.data || [];

      // Extract unique categories from posts
      const uniqueCategories = [
        ...new Set(blogPosts.map((p) => p.category).filter(Boolean)),
      ];

      const categoriesList = [
        { id: "all", name: "All Insights" },
        ...uniqueCategories.map((cat) => ({
          id: cat,
          name: cat.charAt(0).toUpperCase() + cat.slice(1),
        })),
      ];

      setCategories(categoriesList);
    } catch (error) {
      console.error("Error loading categories:", error);
      // Fallback categories
      setCategories([
        { id: "all", name: "All Insights" },
        { id: "leadership", name: "Leadership" },
        { id: "startups", name: "Startups" },
        { id: "growth", name: "Growth" },
      ]);
    }
  };

  const fetchBlogPosts = async () => {
    setLoading(true);
    try {
      const response = await blogApi.getAll(true); // Get published posts only
      const blogPosts = response.data || [];

      // Sort by published date (newest first)
      const sortedPosts = blogPosts.sort((a, b) => {
        const dateA = new Date(a.published_at);
        const dateB = new Date(b.published_at);
        return dateB - dateA;
      });

      setPosts(sortedPosts);
    } catch (error) {
      console.error("Error loading blog posts:", error);
      alert("Failed to load insights. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const filteredPosts =
    selectedCategory === "all"
      ? posts
      : posts.filter((p) => p.category === selectedCategory);

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  const calculateReadTime = (content) => {
    if (!content) return "5 min read";
    const wordCount = content.split(/\s+/).length;
    const readTime = Math.ceil(wordCount / 200); // Assume 200 words per minute
    return `${readTime} min read`;
  };

  const handleNewsletterSubscribe = async (e) => {
    e.preventDefault();
    try {
      await homeApi.subscribeNewsletter({ email });
      alert("Subscribed successfully!");
      setEmail("");
    } catch (error) {
      alert("Subscription failed. Please try again.");
    }
  };

  return (
    <div>
      <HeroSection
        title={
          pageData?.hero_headline ||
          pageData?.title ||
          "Become a better leader, transform your business"
        }
        subtitle={
          pageData?.hero_subheadline ||
          pageData?.meta_description ||
          pageData?.content ||
          "Turn people into assets, drive growth with innovation"
        }
        heroImage={pageData?.hero_image}
        useSlider={!!pageData?.use_slider}
        sliderImages={pageData?.slider_images || []}
        currentSlide={currentSlide}
        onSlideChange={setCurrentSlide}
        ctaText={pageData?.cta_text}
        ctaHref={pageData?.cta_link || "/contact"}
      />

      {/* Categories and Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-4 lg:gap-8">
            {/* Sidebar */}
            <div className="lg:col-span-1 mb-8 lg:mb-0">
              <div className="sticky top-8">
                <h2 className="text-lg font-semibold mb-4">Categories</h2>
                <div className="space-y-2">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`block w-full text-left px-4 py-2 rounded-lg transition ${
                        selectedCategory === cat.id
                          ? "bg-blue-600 text-white"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>

                <div className="mt-8 p-6 bg-blue-50 rounded-lg">
                  <h3 className="font-semibold text-blue-800 mb-2">
                    About the Author
                  </h3>
                  <p className="text-blue-700 text-sm">
                    Emon Matin writes from real leadership and transformation
                    work across industries, sharing practical, execution-first
                    thinking on strategy, growth, leadership, and performance
                    systems.
                  </p>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900">
                  {selectedCategory === "all"
                    ? "All Insights"
                    : categories.find((c) => c.id === selectedCategory)?.name}
                </h2>
                <p className="text-gray-600">
                  {filteredPosts.length} article
                  {filteredPosts.length !== 1 ? "s" : ""}
                </p>
              </div>

              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="text-gray-500">Loading insights...</div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {filteredPosts.map((post) => (
                      <div
                        key={post.id}
                        className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition duration-300"
                      >
                        {post.featured_image && (
                          <div className="h-48 bg-gray-100">
                            <img
                              src={mediaService.getFileUrl(post.featured_image)}
                              alt={post.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <div className="p-6">
                          <div className="mb-4">
                            <span className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full">
                              {post.category
                                ? post.category.charAt(0).toUpperCase() +
                                  post.category.slice(1)
                                : "Uncategorized"}
                            </span>
                          </div>

                          <h3 className="text-xl font-bold text-gray-900 mb-3">
                            <Link
                              to={`/insights/${post.slug}`}
                              className="hover:text-blue-600"
                            >
                              {post.title}
                            </Link>
                          </h3>

                          <p className="text-gray-600 mb-4">{post.excerpt}</p>

                          <div className="flex items-center justify-between mt-6">
                            <div className="flex items-center">
                              <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center mr-3">
                                <span className="text-gray-600 text-sm">
                                  MA
                                </span>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {post.author || "Mohammad Abdul Matin Emon"}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {formatDate(post.published_at)} •{" "}
                                  {calculateReadTime(post.content)}
                                </p>
                              </div>
                            </div>

                            <Link
                              to={`/insights/${post.slug}`}
                              className="text-blue-600 font-semibold text-sm hover:text-blue-800"
                            >
                              Read More →
                            </Link>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Empty State */}
                  {filteredPosts.length === 0 && (
                    <div className="text-center py-12">
                      <div className="text-gray-400 text-4xl mb-4">📝</div>
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">
                        No articles found
                      </h3>
                      <p className="text-gray-500">
                        Try selecting a different category
                      </p>
                    </div>
                  )}

                  {/* Load More (Demo) */}
                  {filteredPosts.length > 0 && (
                    <div className="mt-12 text-center">
                      <button className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition">
                        Load More Articles
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Stay Updated</h2>
          <p className="mb-8">
            Join our mailing list for the latest insights, program updates, and
            exclusive content.
          </p>
          <form
            onSubmit={handleNewsletterSubscribe}
            className="max-w-md mx-auto"
          >
            <div className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-lg text-gray-800"
                required
              />
              <button
                type="submit"
                className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition"
              >
                Subscribe
              </button>
            </div>
            <p className="text-sm text-blue-100 mt-2">
              We respect your privacy. Unsubscribe at any time.
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}

export default InsightsPage;
